import React from 'react';
import {
  SlSidebarTitle,
  SlSidebarSectionContainer,
  SlSidebar,
  SlSidebarScrollableContainer
} from 'src/components/BeaconV2Components/SlSidebar';
import { useMainEntity, useMainEntityName, useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { SlShowMore } from 'src/components/BeaconV2Components/SlShowMore';
import { TopicGroup, Topic } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/types';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import ChipItem from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/ChipItem';
import { useAppSelector } from 'src/utils/Hooks';
import ReduxStore from 'src/types/store/reduxStore';
import { DisabledCheckbox } from 'src/components/SvgIcons';
import { useStacklineTheme } from '@stackline/ui';
import SidebarInput from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/SidebarInput';
import CheckboxItem from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/CheckboxItem';

/**
 * Sidebar used for standard topics and topic groups. It is readonly
 * and displays the name, phrases, categories, and subcategories
 * included in the topic or topic group.
 */
export function StandardTopicSettingsSidebar() {
  const name = useMainEntityName();
  const entity = useMainEntity() as unknown as TopicGroup | Topic;
  const entityType = useMainEntityType();
  const theme = useStacklineTheme();

  const { includedCategoryIds, includedSubCategoryIds } = entity;

  const categories = useAppSelector((state) => state.categories);
  const subcategories = useAppSelector((state) => state.subCategories);

  // Map category ID to category for constant time lookup
  const categoryIdMap = categories.reduce((acc, category) => {
    acc[category.id] = category;
    return acc;
  }, {} as Record<number, ReduxStore['categories'][0]>);

  // Map subcategory ID to subcategory for constant time lookup
  const subcategoryIdMap = subcategories.reduce((acc, subcategory) => {
    acc[subcategory.id] = subcategory;
    return acc;
  }, {} as Record<number, ReduxStore['subCategories'][0]>);

  return (
    <SlSidebar>
      <SlSidebarSectionContainer>
        <SlSidebarTitle>Name</SlSidebarTitle>
        <SidebarInput disabled value={name} />
      </SlSidebarSectionContainer>
      {entityType === 'topic' && (entity.includedKeywords ?? []).length > 0 && (
        <SlSidebarSectionContainer gap={12}>
          <SlSidebarTitle>Include - Phrase</SlSidebarTitle>
          <SlSidebarScrollableContainer sx={{ marginTop: '0px' }}>
            <SlShowMore
              itemsPerChunk={20}
              items={entity.includedKeywords}
              renderItems={(items) => (
                <Flex alignItems="flex-start" flexDirection="column" gap={12}>
                  {items.map((item: string) => (
                    <ChipItem key={item} label={item} />
                  ))}
                </Flex>
              )}
            />
          </SlSidebarScrollableContainer>
        </SlSidebarSectionContainer>
      )}
      {(includedCategoryIds ?? []).length > 0 && (
        <SlSidebarSectionContainer sx={{ gap: '12px' }}>
          <SlSidebarTitle>Categories</SlSidebarTitle>
          <SlSidebarScrollableContainer sx={{ marginTop: '0px' }}>
            <SlShowMore
              itemsPerChunk={20}
              items={includedCategoryIds ?? []}
              renderItems={(items: number[]) => {
                return (
                  <Flex flexDirection="column" gap={12}>
                    {items.map((categoryId) => {
                      const categoryName = categoryIdMap[categoryId]?.name ?? '';
                      if (categoryName === '') {
                        return null;
                      }

                      return (
                        <CheckboxItem
                          disabled
                          value={categoryId}
                          key={`category-${categoryId}`}
                          label={categoryName}
                          theme={theme}
                          checked
                          checkedIcon={<DisabledCheckbox height="16px" width="16px" />}
                        />
                      );
                    })}
                  </Flex>
                );
              }}
            />
          </SlSidebarScrollableContainer>
        </SlSidebarSectionContainer>
      )}
      {(includedSubCategoryIds ?? []).length > 0 && (
        <SlSidebarSectionContainer sx={{ gap: '12px' }}>
          <SlSidebarTitle>Subcategories</SlSidebarTitle>
          <SlSidebarScrollableContainer sx={{ marginTop: '0px' }}>
            <SlShowMore
              itemsPerChunk={20}
              items={includedSubCategoryIds ?? []}
              renderItems={(items: number[]) => {
                return (
                  <Flex flexDirection="column" gap={12}>
                    {items.map((subcategoryId) => {
                      const subcategoryName = subcategoryIdMap[subcategoryId]?.name ?? '';
                      if (subcategoryName === '') {
                        return null;
                      }

                      return (
                        <CheckboxItem
                          disabled
                          value={subcategoryId}
                          key={`subcategoryId-${subcategoryId}`}
                          label={subcategoryName}
                          theme={theme}
                          checked
                          checkedIcon={<DisabledCheckbox height="16px" width="16px" />}
                        />
                      );
                    })}
                  </Flex>
                );
              }}
            />
          </SlSidebarScrollableContainer>
        </SlSidebarSectionContainer>
      )}
    </SlSidebar>
  );
}
